import { IconCheck, IconSelector } from "@tabler/icons-react";
import type { ReactNode } from "react";
import {
  Button,
  Collection,
  Header,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  Select,
  type Key,
} from "react-aria-components";
import { Size } from "../helpers/helpers.js";
import { Label } from "../Label/label.js";
import { menuStyle } from "../Menu/menuStyle.js";
import { SimpleMenuPopover } from "../Menu/simpleMenuPopover.js";
import { selectStyle } from "./style.js";

/** String value used to represent the null/empty option. */
const nullKey = "_null";
const valueToKey = (value: Key | null) => (value === null ? nullKey : value);
const keyToValue = (key: Key) => (key === nullKey ? null : key);

export interface SelectBaseOption<T extends Key | null> {
  value: T;
  icon?: ReactNode;
  label: string;
}

export interface SelectBaseSection<
  T extends Key | null,
  O extends SelectBaseOption<T>,
> {
  id: string;
  /** Omitted when the section has no header. */
  label?: string;
  options: readonly O[];
}

interface SelectBaseProps<T extends Key | null, O extends SelectBaseOption<T>> {
  leftAccessory?: ReactNode;
  label?: string;
  isLabelHidden?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
  size?: typeof Size.SMALL | typeof Size.MEDIUM;
  isFullWidth?: boolean;
  popoverClassName?: string;
  placeholder?: ReactNode;
  options: readonly O[];
  /** Grouped rendering of `options` with section headers. Must partition `options`. */
  sections?: readonly SelectBaseSection<T, O>[];
  disabledValues?: readonly T[];
  value: T | null;
  onChange: (value: T) => void;
  renderOption: (option: O) => ReactNode;
}

/** Shared select behavior and chrome. Public select components own the visual
 * treatment of their option content through `renderOption`. */
export const SelectBase = <T extends Key | null, O extends SelectBaseOption<T>>(
  props: SelectBaseProps<T, O>,
) => {
  const selectedOption = props.options.find(
    (option) => option.value === props.value,
  );

  const style = selectStyle({
    isInvalid: props.isInvalid,
    size: props.size,
  });

  // Deselecting the current item (an empty selection) is only allowed when a
  // null-valued "None" option makes empty a legitimate state.
  const disallowEmptySelection = props.options.every(
    (option) => option.value !== null,
  );

  const renderListBoxItem = (option: O) => (
    <ListBoxItem
      id={valueToKey(option.value)}
      textValue={option.label}
      className={menuStyle().item()}
    >
      {option.icon ? <div className="shrink-0">{option.icon}</div> : null}
      {props.renderOption(option)}
      <IconCheck
        className={menuStyle().itemIcon({
          className: "invisible text-indigo-600 group-selected:visible",
        })}
      />
    </ListBoxItem>
  );

  return (
    <Select
      className={!props.isLabelHidden && props.label ? "min-w-0" : "contents"}
      isDisabled={props.isDisabled}
      isInvalid={props.isInvalid}
      disabledKeys={props.disabledValues?.map(valueToKey)}
      value={valueToKey(props.value)}
      onChange={(key) => {
        const value = key === null ? null : keyToValue(key);
        const option = props.options.find((option) => option.value === value);
        if (!option) {
          throw new Error("Selected key not found in options");
        }

        props.onChange(option.value);
      }}
      aria-label={props.isLabelHidden ? props.label : undefined}
    >
      {!props.isLabelHidden && props.label ? (
        <Label size={Size.SMALL} className="mb-2">
          {props.label}
        </Label>
      ) : null}
      <Button
        className={style.button({
          className: props.isFullWidth ? "w-full" : undefined,
        })}
      >
        {props.leftAccessory ? (
          <div className="shrink-0 text-gray-700">{props.leftAccessory}</div>
        ) : null}
        {selectedOption &&
        (selectedOption.value !== null || !props.placeholder) ? (
          <>
            {selectedOption.icon ? (
              <div className="shrink-0 text-gray-700">
                {selectedOption.icon}
              </div>
            ) : null}
            <div className={style.value()}>{selectedOption.label}</div>
          </>
        ) : (
          <div className={style.placeholder()}>{props.placeholder}</div>
        )}
        <IconSelector className={style.chevron()} />
      </Button>
      <SimpleMenuPopover className={props.popoverClassName}>
        {props.sections ? (
          <ListBox
            items={props.sections}
            disallowEmptySelection={disallowEmptySelection}
          >
            {(section) => (
              <ListBoxSection id={section.id}>
                {section.label ? (
                  <Header className={menuStyle().sectionHeader()}>
                    {section.label}
                  </Header>
                ) : null}
                <Collection items={section.options}>
                  {renderListBoxItem}
                </Collection>
              </ListBoxSection>
            )}
          </ListBox>
        ) : (
          <ListBox
            items={props.options}
            disallowEmptySelection={disallowEmptySelection}
          >
            {renderListBoxItem}
          </ListBox>
        )}
      </SimpleMenuPopover>
    </Select>
  );
};
